import Qs from 'qs';
import { createAuthHttpClient } from './auth-http-client';
import { AxiosRequestConfig, type AxiosResponse } from 'axios';
import { AuthStore } from '../storage/auth-storage-manager';
import { TokenResponse } from 'auth/types/token-response.type';
import { UserInfo } from 'auth/types/user-info.type';
import { AuthConfig } from 'types/AuthConfig.type';
import { useConfig } from 'providers/config/ConfigProvider';
import { getCurrentAuthMethod } from 'auth/auth-methods/current-auth-method';

const DEFAULT_POST_AUTH_PATH = '/details';

export const POST_AUTH_AF_SSO_REDIRECT_KEY = 'POST_AUTH_AF_SSO_REDIRECT';

export const getPostAuthNavigationPath = (): string => {
  const searchQueryParameter = Qs.parse(location.search.substring(1));
  const stateQueryParamter = searchQueryParameter['state'];
  const stateQueryString: string | undefined =
    typeof stateQueryParamter === 'string'
      ? stateQueryParamter
      : Array.isArray(stateQueryParamter)
        ? stateQueryParamter.at(0)?.toString()
        : undefined;

  const postAuthNavigationPath =
    stateQueryString === '/'
      ? DEFAULT_POST_AUTH_PATH
      : (stateQueryString ?? DEFAULT_POST_AUTH_PATH);
  return postAuthNavigationPath;
};

export const getPostAuthNavigationPathAfSso = (): string => {
  const postAuthRedirect = localStorage.getItem(POST_AUTH_AF_SSO_REDIRECT_KEY);
  const finalRedirect =
    postAuthRedirect === '/'
      ? DEFAULT_POST_AUTH_PATH
      : (postAuthRedirect ?? DEFAULT_POST_AUTH_PATH);
  return finalRedirect;
};

export const getPostAuthCodeAndVerifier = () => {
  const search = Qs.parse(location.search.substring(1));
  const codeQueryParameter = search['code'];
  const code = typeof codeQueryParameter === 'string' ? codeQueryParameter : undefined;
  const codeVerifier = AuthStore.getCodeVerifier();
  return { code, codeVerifier };
};

export const getPostOAuthToken = async (
  code: string,
  codeVerifier: string,
  authConfig: AuthConfig
): Promise<TokenResponse | undefined> => {
  const env = useConfig();
  const { isUsingAfSso } = getCurrentAuthMethod(env);

  const auth = isUsingAfSso ? authConfig.afSso : authConfig.client;
  const tokenClient = createAuthHttpClient({ clientId: auth.clientId });
  const tokenRequest: AxiosRequestConfig = {
    url: auth.tokenProvider,
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    data: Qs.stringify({
      grant_type: 'authorization_code',
      client_id: auth.clientId,
      redirect_uri: `${window.location.origin}${auth.redirectUriPath}`,
      code: code,
      code_verifier: codeVerifier
    })
  };

  const tokenCreationTime = new Date();
  let token: TokenResponse | undefined = undefined;

  try {
    const tokenHttpResponse: AxiosResponse<TokenResponse> =
      await tokenClient<TokenResponse>(tokenRequest);
    AuthStore.setCodeVerifier(undefined);
    if (tokenHttpResponse.status != 200) return;
    token = tokenHttpResponse.data;
  } catch (_error) {
    return;
  }

  if (token) {
    const tokenExpiryTime = tokenCreationTime;
    tokenExpiryTime.setSeconds(tokenCreationTime.getSeconds() + token.expires_in);
    token.token_expiry_time = tokenExpiryTime;
  }

  return token;
};

export const getPostAuthUserInfo = async (
  accessToken: string,
  userInfoProviderUrl: string
): Promise<UserInfo | undefined> => {
  const userClient = createAuthHttpClient({ bearerAccessToken: accessToken });

  const userinfoRequest: AxiosRequestConfig = {
    url: userInfoProviderUrl,
    method: 'GET'
  };

  let userInfo: UserInfo | undefined = undefined;

  try {
    const userinfoResponse: AxiosResponse<UserInfo> = await userClient<UserInfo>(userinfoRequest);
    userInfo = userinfoResponse.data;
    if (userinfoResponse.status != 200) return;
  } catch (_error) {
    return;
  }

  return userInfo;
};

export const generateNonce = (length: number = 41): string => {
  var text = '';
  var possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  for (let i = 0; i < length; i++) {
    text += possible.charAt(Math.floor(Math.random() * possible.length));
  }
  return text;
};
